import _partition from 'lodash/partition';
import getValidatedMappingCards from './getValidatedMappingCards';
import MappingProps, * as MappingTypes from 'types/Mapping';
import * as Sift from '@sift/types';
import {
  DEFAULT_EMPTY_ORG_MAPPINGS,
  ORG_TEAM_LEADER_MAPPING_REF_KEY,
  ORG_TEAM_LEADER_MAPPING_MATCH_KEY,
  PRIMARY_MAPPING_TYPE_KEY,
  POSITION_ID_MAPPING_TYPE_KEY,
  ORG_MAPPING_TYPE_KEY,
  PROFILE_ATTR_MAPPING_TYPE_KEY,
} from 'pages/Directory/consts';

export default function createMappingsCardData(
  sourceMappings: MappingProps[],
  validationResults: MappingTypes.MappingValidationResult[],
  userFields: Sift.Field[],
) {
  const [identifierMappings, nonIdentifierMappings] = _partition(
    sourceMappings,
    ({ type }) => type === PRIMARY_MAPPING_TYPE_KEY || type === POSITION_ID_MAPPING_TYPE_KEY,
  );

  const partitionHierarchy = ({ destination, type }: MappingTypes.MappingAttribute) => {
    const matchesHierarchy =
      destination === ORG_TEAM_LEADER_MAPPING_REF_KEY ||
      destination === ORG_TEAM_LEADER_MAPPING_MATCH_KEY;
    return matchesHierarchy && type !== 'hierarchy';
  };
  const [_hierarchyMappings, nonHierarchyTypes] = _partition(
    nonIdentifierMappings,
    partitionHierarchy,
  );
  // (note - pearl) Have tlRef mapping to be index 0, and tlMatch mapping to be index 1
  // reference TL_MATCH_MAPPING_INDEX and TL_REF_MAPPING_INDEX in 'pages/Directory/consts'
  const hierarchyMappings = _hierarchyMappings.length
    ? [
        // @ts-ignore
        _hierarchyMappings.find(m => m.destination === ORG_TEAM_LEADER_MAPPING_MATCH_KEY),
        // @ts-ignore
        _hierarchyMappings.find(m => m.destination === ORG_TEAM_LEADER_MAPPING_REF_KEY),
      ]
    : DEFAULT_EMPTY_ORG_MAPPINGS;

  const partitionIsRequired = ['isRequired', true];
  const [isRequiredMappings, dataMappings]: any[] = _partition(
    nonHierarchyTypes,
    partitionIsRequired,
  );

  const partitionFilteredFields = ({ type }: MappingTypes.MappingAttribute) => {
    return type !== 'hierarchy';
  };
  const [filteredFields, __]: any[] = _partition(dataMappings, partitionFilteredFields);

  const displayedDataMappings = filteredFields.filter((mapping: any) => {
    // (note - pearl) if no destination (recommended mapping), then skip this check
    return mapping.destination ? userFields[mapping.destination] : true;
  });

  const _mappingCards = [
    {
      title: 'Primary Key',
      initialMappings: identifierMappings,
      mappings: identifierMappings,
      type: PRIMARY_MAPPING_TYPE_KEY,
      id: PRIMARY_MAPPING_TYPE_KEY,
      // (NOTE - Dylan) This copy does not seem to be displayed anywhere, and we should look to remove it in the future
      description:
        'The Primary Key is the unique identifier for all of your people. Anyone with a non-unique primary key value will not be imported. Note: This cannot be changed after your first sync without contacting our support team. Still need help? View our documentation.',
    },
    {
      title: 'Set Up Org Chart (Optional)',
      initialMappings: hierarchyMappings,
      mappings: hierarchyMappings,
      type: ORG_MAPPING_TYPE_KEY,
      id: ORG_MAPPING_TYPE_KEY,
      description:
        'The team leader hierarchy mapping is used to build your organizational structure in Sift. This will allow you to use features like our org chart. On the left side of this mapping, provide which property on a person represents a reference to their direct manager in your system (Ex: "managerEmail"). On the right side of this mapping, type which property the above property points to on another person in your system (Ex: "email").',
    },
    {
      title: 'Profile Attributes',
      initialMappings: [...isRequiredMappings, ...displayedDataMappings],
      mappings: [...isRequiredMappings, ...displayedDataMappings],
      type: PROFILE_ATTR_MAPPING_TYPE_KEY,
      id: PROFILE_ATTR_MAPPING_TYPE_KEY,
      description:
        'Attributes are used to identify different characteristics of your user population. The three required attributes are First Name, Last Name, and Email, these are used as base markers for your users and must be mapped below. To learn more about attributes, visit our help center.',
    },
  ];

  const mappingCards = getValidatedMappingCards(_mappingCards, validationResults);

  return mappingCards;
}
